/**
 * IslandRegistry — host-declared map from island name to React component.
 *
 * Islands are the escape hatch for ui.cogo.json nodes whose subtree is owned
 * by bespoke React code (e.g. Studio's `<LeftPanel />`, `<EntityTree />`,
 * `<StoryNavPanelV2 />`). The renderer looks up the component by name and
 * mounts it with `resolveSymbol`-evaluated props, but does not descend into
 * its render tree.
 *
 * See .planning/feature-island-design.md for the design notes — in particular
 * §3 for the lifecycle rules and §5 for the security posture.
 *
 * Islands are trusted host code. Flow JSON cannot register, invoke, or unload
 * islands — this surface is host-only.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from "react"

export type IslandComponent = ComponentType<Record<string, unknown>>

/** Loader shape compatible with both native dynamic `import()` (which resolves
 * to `{ default: Component }`) and ad-hoc factories that return either
 * `{ default }` or the component directly. */
export type LazyIslandLoader = () => Promise<
  { default: IslandComponent } | IslandComponent
>

export type IslandEntry = IslandComponent | LazyExoticComponent<IslandComponent>

export class IslandRegistry {
  private entries = new Map<string, IslandEntry>()

  /** Register a ready-to-mount React component. Overrides any existing entry. */
  register(name: string, component: IslandComponent): void {
    this.entries.set(name, component)
  }

  /** Register a lazy loader. Wraps in `React.lazy` so the renderer's Suspense
   * boundary handles the loading state. The loader may resolve to either
   * `{ default }` (standard dynamic-import shape) or the component directly. */
  registerLazy(name: string, loader: LazyIslandLoader): void {
    const Wrapped = lazy(async () => {
      const mod = await loader()
      if (mod && typeof mod === "object" && "default" in mod) {
        return mod as { default: IslandComponent }
      }
      return { default: mod as IslandComponent }
    })
    this.entries.set(name, Wrapped)
  }

  /** Register many ready-to-mount components in one call. Ignores entries
   * whose value is null/undefined (convenience for optional islands). For
   * lazy islands call `registerLazy` per name instead. */
  registerAll(entries: Record<string, IslandComponent | undefined>): void {
    for (const [name, component] of Object.entries(entries)) {
      if (typeof component === "function") {
        this.entries.set(name, component)
      }
    }
  }

  unregister(name: string): void {
    this.entries.delete(name)
  }

  clear(): void {
    this.entries.clear()
  }

  has(name: string): boolean {
    return this.entries.has(name)
  }

  get(name: string): IslandEntry | undefined {
    return this.entries.get(name)
  }

  /** Sorted snapshot — useful for DevTools hints and validator suggestions. */
  list(): string[] {
    return [...this.entries.keys()].sort()
  }
}
